import React, { useState } from "react";
import { Button, Card, Form, Input, Layout, Spin, Typography, message, Alert } from "antd";
import { LockOutlined, UserOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { resolveApiBase } from "../lib/apiBase";
import { consumePostLoginRedirect } from "../lib/sessionExpiry";

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;

const API_BASE = resolveApiBase();

const LoginPage = ({ onLoginSuccess }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [form] = Form.useForm();

    const handleLogin = async (values) => {
        setError(null);
        setLoading(true);

        try {
            const res = await fetch(`${API_BASE}/auth/login`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    username: values.username,
                    password: values.password,
                }),
            });

            if (!res.ok) {
                const errorData = await res.json();
                throw new Error(errorData.detail || `Login failed (${res.status})`);
            }

            const data = await res.json();

            // Store in localStorage
            localStorage.setItem("actor", data.actor);
            localStorage.setItem("role", data.role);
            localStorage.setItem("token", data.token);
            localStorage.setItem("username", data.username);

            message.success(`Welcome, ${data.username}!`);
            onLoginSuccess?.();
            navigate(consumePostLoginRedirect() || "/");
        } catch (err) {
            setError(err.message || "Login failed");
            message.error(err.message || "Login failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Layout style={{ minHeight: "100vh", background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
            <Content
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "20px",
                }}
            >
                <Card
                    style={{
                        width: "100%",
                        maxWidth: 400,
                        boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
                    }}
                    variant="borderless"
                >
                    <div style={{ textAlign: "center", marginBottom: 32 }}>
                        <div style={{ fontSize: 32, marginBottom: 16 }}>
                            <ThunderboltOutlined style={{ color: "#722ed1" }} />
                        </div>
                        <Title level={2} style={{ margin: 0, marginBottom: 8 }}>
                            DataSnare AIOps
                        </Title>
                        <Text type="secondary">Automated Incident Operations Platform</Text>
                    </div>

                    {error && (
                        <Alert
                            message="Login Error"
                            description={error}
                            type="error"
                            showIcon
                            closable
                            onClose={() => setError(null)}
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    <Spin spinning={loading}>
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={handleLogin}
                            autoComplete="off"
                        >
                            <Form.Item
                                name="username"
                                label="Username"
                                rules={[
                                    { required: true, message: "Username required" },
                                    { min: 1, message: "Username required" },
                                ]}
                            >
                                <Input
                                    prefix={<UserOutlined />}
                                    placeholder="Enter username"
                                    autoComplete="username"
                                    disabled={loading}
                                />
                            </Form.Item>

                            <Form.Item
                                name="password"
                                label="Password"
                                rules={[
                                    { required: true, message: "Password required" },
                                    { min: 1, message: "Password required" },
                                ]}
                            >
                                <Input.Password
                                    prefix={<LockOutlined />}
                                    placeholder="Enter password"
                                    autoComplete="current-password"
                                    disabled={loading}
                                />
                            </Form.Item>

                            <Form.Item>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    block
                                    size="large"
                                    loading={loading}
                                    style={{ background: "#722ed1", borderColor: "#722ed1" }}
                                >
                                    {loading ? "Signing in..." : "Sign In"}
                                </Button>
                            </Form.Item>
                        </Form>
                    </Spin>

                    <div style={{ marginTop: 24, padding: "16px", background: "#f5f5f5", borderRadius: 4 }}>
                        <Paragraph style={{ marginBottom: 0, fontSize: 12 }}>
                            New user invite? Open the email link you received to set your password.
                        </Paragraph>
                    </div>
                </Card>
            </Content>
        </Layout>
    );
};

export default LoginPage;
